import React, {Component, PropTypes} from 'react';
import is from 'is';

import CurrentUserStore from '../stores/CurrentUserStore';


let _lastAuthTransitionPath = null;

/**
 *
 * @param {Component} ChildComponent
 * @param {function} predicate
 * @param {function} redirectUrl
 * @return {AuthStateView}
 */
function makeView(ChildComponent, predicate, redirectUrl) {
    let redirectFunc;
    if (!is.fn(redirectUrl)) {
        redirectFunc = () => {
            return redirectUrl;
        };
    } else {
        redirectFunc = redirectUrl;
    }

    class AuthStateView extends Component {
        static contextTypes = {
            router: PropTypes.func.isRequired
        };

        static willTransitionTo(transition, params, query, callback) {
            _lastAuthTransitionPath = transition.path;

            const state = CurrentUserStore.getState();
            if (AuthStateView.predicate(state.isAuthenticated)) {
                transition.redirect(AuthStateView.redirectUrl());
            }
            callback();
        }

        render() {
            const isPro = () => CurrentUserStore.isPro();
            const isBsn = () => CurrentUserStore.isBsn();

            return (<ChildComponent isPro={isPro} isBsn={isBsn} {...this.props} />);
        }
    }

    AuthStateView.predicate = predicate;
    AuthStateView.redirectUrl = redirectFunc;

    return AuthStateView;
}


/**
 * 'Higher Order Component' that checks that the user is not
 *  authenticated and redirects to specified url if she is.
 *
 *  Can be used directly:
 *
 *   >>> anonymousView('home')(MyComponent)
 *
 *  or as a ES7 decorator:
 *
 *   >>> @anonymousView('home')
 *   >>> class MyComponent extends Component {...}
 * @param redirectUrl
 */
export function anonymousView(redirectUrl) {
    if (!redirectUrl) {
        redirectUrl = () => {
            return DJ_CONST.LOGIN_REDIRECT;
        };
    }

    return function decorator(ChildComponent) {
        return makeView(ChildComponent, authenticated => authenticated, redirectUrl);
    };
}


/**
 * 'Higher Order Component' that checks that the user is
 *  authenticated and redirects to specified url if she is not.
 *
 *  Can be used directly:
 *
 *   >>> authenticatedView('home')(MyComponent)
 *
 *  or as a ES7 decorator:
 *
 *   >>> @authenticatedView('home')
 *   >>> class MyComponent extends Component {...}
 */
export function authenticatedView(redirectUrl) {
    return function decorator(ChildComponent) {
        return makeView(ChildComponent, authenticated => !authenticated, redirectUrl);
    };
}


/**
 * 'Higher Order Component' that checks if user account is the
 *  allowed type to see that view, redirects, if not.
 *
 *  Can be used directly:
 *
 *   >>> appView('pro', 'home')(Component)
 *
 *  or as a ES7 decorator:
 *
 *   >>> @appView('pro', 'home')
 *   >>> class Component extends React.component {...}
 */
export function appView(userRole, redirectUrl) {
    return function decorator(Component) {
        class AnonymousView extends Component {
            static willTransitionTo(transition, params, query, callback) {
                _lastAuthTransitionPath = transition.path;

                const isCorrectRole = CurrentUserStore[userRole]();
                if (! isCorrectRole) {
                    transition.redirect(redirectUrl);
                }

                callback();
            }

            render() {
                const isPro = () => CurrentUserStore.isPro();
                const isBsn = () => CurrentUserStore.isBsn();
                return (<Component isPro={isPro} isBsn={isBsn} {...this.props} />);
            }
        }

        return AnonymousView;
    };
}


export function getLoginRedirectUrl() {
    const isBsn = () => CurrentUserStore.isBsn();
    const isPro = () => CurrentUserStore.isPro();
    const link = isBsn() ? '/dashboard' : '/offers';
    return _lastAuthTransitionPath;
}



/** WEBPACK FOOTER **
 ** ./app/src/utils/authViewHelpers.js
 **/